import {
  type ComplianceDocumentsLocaleKey,
  normalizeComplianceDocumentsUiLocale,
} from '@artem-lex/compliance-documents'

export type HazardLocalizationFields = {
  description?: string
  category?: string
  examplesOfHazardousSituations?: string
  associatedActivity?: string
  relatedDangerZone?: string
}

export type InformationForUseClauseLocalizationFields = {
  title?: string
  fullStatement?: string
  sectionTitle?: string
}

export type RiskReductionMeasureLocalizationFields = {
  name?: string
  description?: string
}

export type StandardEntityLocalizations<T> = Partial<
  Record<ComplianceDocumentsLocaleKey, T>
>

/** Display sentinel: show original extracted PDF text (top-level fields). */
export const STANDARD_ENTITY_ORIGINAL_VIEW = 'original' as const

export type EntityDisplayLocale =
  | ComplianceDocumentsLocaleKey
  | typeof STANDARD_ENTITY_ORIGINAL_VIEW

/**
 * @deprecated Use {@link STANDARD_ENTITY_ORIGINAL_VIEW} for original-text display.
 * Kept temporarily for callers that still reference the old name.
 */
export const STANDARD_ENTITY_SOURCE_LOCALE = STANDARD_ENTITY_ORIGINAL_VIEW

const COMPLIANCE_DOCUMENT_LOCALES: ComplianceDocumentsLocaleKey[] = [
  'en-GB',
  'de-DE',
  'zh-CN',
]

function hasNonEmptyString(value: string | undefined): boolean {
  return value != null && value.trim() !== ''
}

function hasLocalizedFieldContent(
  fields: Record<string, string | undefined> | undefined
): boolean {
  if (fields == null) {
    return false
  }
  return Object.values(fields).some(hasNonEmptyString)
}

export function resolveLocalesFromLocalizations<
  T extends Record<string, string | undefined>,
>(
  localizations: StandardEntityLocalizations<T> | undefined
): ComplianceDocumentsLocaleKey[] {
  if (localizations == null) {
    return []
  }
  return COMPLIANCE_DOCUMENT_LOCALES.filter((locale) =>
    hasLocalizedFieldContent(localizations[locale])
  )
}

/** Locales with stored translations, including English when explicitly localized. */
export function resolveStoredLocalesFromLocalizations<
  T extends Record<string, string | undefined>,
>(
  localizations: StandardEntityLocalizations<T> | undefined
): ComplianceDocumentsLocaleKey[] {
  return resolveLocalesFromLocalizations(localizations)
}

/** @deprecated Use {@link resolveStoredLocalesFromLocalizations}. */
export function resolveAdditionalLocalesFromLocalizations<
  T extends Record<string, string | undefined>,
>(
  localizations: StandardEntityLocalizations<T> | undefined
): ComplianceDocumentsLocaleKey[] {
  return resolveStoredLocalesFromLocalizations(localizations)
}

export type HazardLocaleSource = {
  description: string
  category?: string
  examplesOfHazardousSituations?: string
  associatedActivity?: string
  relatedDangerZone?: string
  localizations?: StandardEntityLocalizations<HazardLocalizationFields>
}

export function resolveHazardAvailableLocales(
  hazard: HazardLocaleSource
): ComplianceDocumentsLocaleKey[] {
  return resolveStoredLocalesFromLocalizations(hazard.localizations)
}

export type InformationForUseClauseLocaleSource = {
  title?: string
  fullStatement?: string
  sectionTitle?: string
  content?: string
  localizations?: StandardEntityLocalizations<InformationForUseClauseLocalizationFields>
}

export type RiskReductionMeasureLocaleSource = {
  name: string
  description: string
  localizations?: StandardEntityLocalizations<RiskReductionMeasureLocalizationFields>
}

export function resolveInformationForUseAvailableLocales(
  clause: InformationForUseClauseLocaleSource
): ComplianceDocumentsLocaleKey[] {
  return resolveStoredLocalesFromLocalizations(clause.localizations)
}

export function resolveRiskReductionMeasureAvailableLocales(
  measure: RiskReductionMeasureLocaleSource
): ComplianceDocumentsLocaleKey[] {
  return resolveStoredLocalesFromLocalizations(measure.localizations)
}

export function resolveUiLocale(
  i18nLanguage: string | undefined
): ComplianceDocumentsLocaleKey {
  return normalizeComplianceDocumentsUiLocale(i18nLanguage)
}

function pickLocalized(
  localized: string | undefined,
  source: string | undefined
): string {
  if (localized != null && localized.trim() !== '') {
    return localized
  }
  return source ?? ''
}

export function resolveLocalizedHazardField(
  hazard: HazardLocaleSource,
  locale: EntityDisplayLocale | undefined,
  field: keyof HazardLocalizationFields
): string {
  if (locale === STANDARD_ENTITY_ORIGINAL_VIEW) {
    return hazard[field] ?? ''
  }
  if (locale != null) {
    const localized = hazard.localizations?.[locale]?.[field]
    return pickLocalized(localized, hazard[field])
  }
  return hazard[field] ?? ''
}

export function resolveLocalizedInformationForUseField(
  clause: InformationForUseClauseLocaleSource,
  locale: EntityDisplayLocale | undefined,
  field: keyof InformationForUseClauseLocalizationFields
): string {
  if (locale === STANDARD_ENTITY_ORIGINAL_VIEW) {
    return clause[field] ?? ''
  }
  if (locale != null) {
    const localized = clause.localizations?.[locale]?.[field]
    return pickLocalized(localized, clause[field])
  }
  return clause[field] ?? ''
}

export function resolveLocalizedRiskReductionMeasureField(
  measure: RiskReductionMeasureLocaleSource,
  locale: EntityDisplayLocale | undefined,
  field: keyof RiskReductionMeasureLocalizationFields
): string {
  if (locale === STANDARD_ENTITY_ORIGINAL_VIEW) {
    return measure[field] ?? ''
  }
  if (locale != null) {
    const localized = measure.localizations?.[locale]?.[field]
    return pickLocalized(localized, measure[field])
  }
  return measure[field] ?? ''
}
